import React, { useState, useEffect, useRef, useCallback } from "react";
import { m as motion, MAnimatePresence as AnimatePresence } from "../../shared/components/Motion";
import "./header.css";
import {
  containerVariants,
  childVariants,
  modalVariantsForThemes,
} from "../../shared/utils";
import { useAdmin } from "../../features/admin/AdminProvider";
const navItems = [
  { id: "home", text: "Home", icon: "estate" },
  { id: "about", text: "About", icon: "user" },
  { id: "skills", text: "Skills", icon: "file-alt" },
  { id: "certificates", text: "Certificates", icon: "briefcase-alt" },
  { id: "projects", text: "Projects", icon: "scenery" },
  { id: "contact", text: "Contact", icon: "message" },
];

// Debounce util
const debounce = (func, delay) => {
  let timeoutId;
  return function (...args) {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => func.apply(this, args), delay);
  };
};

const Header = () => {
  const { isAdmin, adminMode } = useAdmin();
  const [toggle, setToggle] = useState(false);
  const [activeNav, setActiveNav] = useState("#home");
  const [scrollProgress, setScrollProgress] = useState(0);
  const headerRef = useRef(null);
  const navMenuRef = useRef();

  // Close nav on outside click
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (navMenuRef.current && !navMenuRef.current.contains(event.target)) {
        setToggle(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Scroll tracking
  useEffect(() => {
    const handleScroll = () => {
      const header = headerRef.current;
      if (!header) return;

      const sections = document.querySelectorAll("section[id]");
      let newActiveNav = activeNav;

      sections.forEach((section) => {
        const navHeight = header.offsetHeight;
        const sectionTop = section.offsetTop - navHeight;
        const sectionHeight = section.offsetHeight;

        if (
          window.scrollY >= sectionTop &&
          window.scrollY < sectionTop + sectionHeight
        ) {
          newActiveNav = `#${section.id}`;
        }
      });

      if (newActiveNav !== activeNav) {
        setActiveNav(newActiveNav); // update only if changed
      }

      // Progress bar
      const scrollTop = window.scrollY;
      const docHeight =
        document.documentElement.scrollHeight - window.innerHeight;
      const progress = docHeight > 0 ? (scrollTop / docHeight) * 100 : 0;

      if (progress !== scrollProgress) {
        setScrollProgress(progress); // update only if changed
      }
    };

    const debouncedHandleScroll = debounce(handleScroll, 100);
    window.addEventListener("scroll", debouncedHandleScroll);
    return () => window.removeEventListener("scroll", debouncedHandleScroll);
  }, [activeNav, scrollProgress]);

  // Stable nav click handler
  const handleNavClick = useCallback((navId) => {
    setActiveNav(navId);
    setToggle(false);
  }, []);

  return (
    <motion.header
      className="header"
      ref={headerRef}
      initial="hidden"
      animate="visible"
      variants={containerVariants}
    >
      {/* Progress Bar */}
      {scrollProgress > 0 && (
        <>
          <motion.div
            className="header__progress-path"
            initial={{ opacity: 0, x: -10 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.4, ease: "easeOut" }}
            style={{ opacity: 1 }}
          />
          <motion.div
            className="header__progress-bar"
            initial={{ opacity: 0, width: 0 }}
            animate={{ opacity: 1, width: `${scrollProgress}%` }}
            transition={{
              opacity: { duration: 0.3 },
              width: { duration: 0.2, ease: "easeOut" },
            }}
            style={{ width: `${scrollProgress}%`, opacity: 1 }}
          />
        </>
      )}

      <nav className="nav container">
        <motion.a
          href="index.html"
          className="nav__logo"
          variants={childVariants}
        >
          <p className="nav__logo-icon">{`</`}</p>
          Santhosh
          <p className="nav__logo-icon">{`>`}</p>
        </motion.a>

        <motion.div
          ref={navMenuRef}
          className={toggle ? "nav__menu show-menu" : "nav__menu"}
          variants={containerVariants}
        >
          <motion.ul className="nav__list grid" variants={containerVariants}>
            {navItems.map((item) => (
              <MemoNavItem
                key={item.id}
                item={item}
                activeNav={activeNav}
                handleNavClick={handleNavClick}
              />
            ))}
          </motion.ul>

          <motion.i
            className="uil uil-times nav__close"
            onClick={() => setToggle((prev) => !prev)}
          />
        </motion.div>
        {/* MODE INDICATOR - Desktop Only */}
        <AnimatePresence mode="wait">
          {isAdmin && adminMode && (
            <motion.div
              key="edit-indicator"
              className="header__mode-indicator edit-mode"
              variants={modalVariantsForThemes}
              initial="hidden"
              animate="visible"
              exit="exit"
              style={{ position: "absolute", right: "1rem" }}
              aria-label="Edit mode is currently active"
            >
              <i className="bx bx-edit-alt"></i>
              <span className="mode-text">Edit Mode</span>
            </motion.div>
          )}
        </AnimatePresence>
        <motion.div
          className="nav__open"
          onClick={() => setToggle((prev) => !prev)}
        >
          <i className="uil uil-apps"></i>
        </motion.div>
      </nav>
    </motion.header>
  );
};

// Memoized NavItem to prevent re-renders unless props change
const NavItem = ({ item, activeNav, handleNavClick }) => (
  <motion.li className="nav_item" variants={childVariants}>
    <a
      href={`#${item.id}`}
      onClick={() => handleNavClick(`#${item.id}`)}
      className={
        activeNav === `#${item.id}` ? "nav__link active-link" : "nav__link"
      }
    >
      <i className={`uil uil-${item.icon} nav__icon`}></i>
      {item.text}
    </a>
  </motion.li>
);

const MemoNavItem = React.memo(NavItem);

export default Header;
